/* ==========================================================================
   Onyx — theme.js
   Entry point. Registers the global behaviours that every page needs and
   lazily imports the feature modules that only some pages use.
   ========================================================================== */

import {
  config,
  prefersReducedMotion,
  throttle,
  trapFocus,
  releaseFocus,
  lockScroll,
  unlockScroll,
  onSectionLoad,
  isDesignMode,
  once,
} from './utils.js';

/* --------------------------------------------------------------------------
   Scroll reveal
   -------------------------------------------------------------------------- */
function initReveal(root = document) {
  if (!document.body.classList.contains('anim-reveal') || prefersReducedMotion()) {
    document.querySelectorAll('.reveal, .stagger').forEach((el) => el.classList.add('is-visible'));
    return;
  }

  document.documentElement.classList.add('anim-reveal-ready');
  document.body.classList.add('anim-reveal-ready');

  const targets = root.querySelectorAll('.reveal:not(.is-visible), .stagger:not(.is-visible)');
  if (!targets.length) return;

  if (!('IntersectionObserver' in window)) {
    targets.forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        obs.unobserve(entry.target);
      });
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  targets.forEach((el) => observer.observe(el));

  // Failsafe: never leave the page blank if the observer stays silent.
  window.setTimeout(() => {
    document.querySelectorAll('.reveal:not(.is-visible), .stagger:not(.is-visible)').forEach((el) => {
      const box = el.getBoundingClientRect();
      if (box.top < window.innerHeight * 1.5) el.classList.add('is-visible');
    });
  }, 1500);
}

/* --------------------------------------------------------------------------
   Accordion — smooth height without layout thrash (grid-template-rows)
   -------------------------------------------------------------------------- */
function initAccordions(root = document) {
  root.querySelectorAll('[data-accordion-group]').forEach((group) => {
    if (group.dataset.accordionBound === 'true') return;
    group.dataset.accordionBound = 'true';

    group.addEventListener('toggle', (event) => {
      const item = event.target;
      if (!(item instanceof HTMLDetailsElement) || !item.open) return;
      if (group.dataset.accordionGroup !== 'single') return;
      group.querySelectorAll('details[open]').forEach((other) => {
        if (other !== item) other.open = false;
      });
    }, true);
  });
}

/* --------------------------------------------------------------------------
   Drawer / modal controller
   -------------------------------------------------------------------------- */
/* Shared across module instances deliberately. openPanel() can be called from a
   second copy of this file (cart.js imports it) while the Escape and overlay
   listeners belong to the first; a private Set would leave those panels
   unclosable. Written long-hand rather than with ||= to keep the browser floor
   where the rest of the theme puts it. */
const openPanels = window.__onyxOpenPanels || (window.__onyxOpenPanels = new Set());

export function openPanel(panel, opener) {
  if (!panel) return;
  // A second open for one click would lock the scroll twice while a single
  // close only unlocks once, leaving the page permanently unscrollable.
  if (openPanels.has(panel)) return;
  panel.classList.add('is-open');
  panel.removeAttribute('inert');
  panel.setAttribute('aria-hidden', 'false');

  const overlayId = panel.dataset.overlay;
  const overlay = overlayId ? document.getElementById(overlayId) : panel.previousElementSibling;
  if (overlay && overlay.classList.contains('overlay')) overlay.classList.add('is-open');

  if (opener) opener.setAttribute('aria-expanded', 'true');
  panel._opener = opener || document.activeElement;

  lockScroll();
  const focusTarget = panel.querySelector('[data-autofocus]') || panel.querySelector('.drawer__close, .modal__close');
  trapFocus(panel, focusTarget);
  openPanels.add(panel);
  panel.dispatchEvent(new CustomEvent('onyx:panel:open', { bubbles: true }));
}

export function closePanel(panel) {
  if (!panel) return;
  // Evict first. If a panel lost .is-open by some other route, the openPanel
  // guard above would otherwise make it permanently un-openable.
  openPanels.delete(panel);
  if (!panel.classList.contains('is-open')) return;
  panel.classList.remove('is-open');
  panel.setAttribute('aria-hidden', 'true');

  const overlayId = panel.dataset.overlay;
  const overlay = overlayId ? document.getElementById(overlayId) : panel.previousElementSibling;
  if (overlay && overlay.classList.contains('overlay')) overlay.classList.remove('is-open');

  const opener = panel._opener;
  if (opener && opener.hasAttribute('aria-expanded')) opener.setAttribute('aria-expanded', 'false');

  releaseFocus(false);
  unlockScroll();

  if (opener && typeof opener.focus === 'function' && document.contains(opener)) {
    opener.focus({ preventScroll: true });
  }
  panel.dispatchEvent(new CustomEvent('onyx:panel:close', { bubbles: true }));
}

export function closeAllPanels() {
  Array.from(openPanels).forEach(closePanel);
}

function initPanelTriggers() {
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-panel-open]');
    if (opener) {
      const panel = document.getElementById(opener.dataset.panelOpen);
      if (panel) {
        event.preventDefault();
        openPanel(panel, opener);
      }
      return;
    }

    const closer = event.target.closest('[data-panel-close]');
    if (closer) {
      event.preventDefault();
      const explicit = closer.dataset.panelClose ? document.getElementById(closer.dataset.panelClose) : null;
      closePanel(explicit || closer.closest('.drawer, .modal'));
      return;
    }

    if (event.target.classList.contains('overlay') || event.target.classList.contains('modal__backdrop')) {
      closeAllPanels();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    if (!openPanels.size) return;
    const last = Array.from(openPanels).pop();
    closePanel(last);
  });
}

/* --------------------------------------------------------------------------
   Sticky header
   -------------------------------------------------------------------------- */
function initStickyHeader() {
  const header = document.querySelector('[data-header]');
  if (!header) return;

  const mode = header.dataset.sticky || 'always';
  const measure = () => {
    document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
  };
  measure();
  window.addEventListener('resize', throttle(measure, 200));

  if (mode === 'never') return;

  let lastY = window.scrollY;
  const onScroll = () => {
    const y = window.scrollY;
    const threshold = 40;

    header.classList.toggle('is-scrolled', y > threshold);

    if (mode === 'scroll-up') {
      const goingDown = y > lastY && y > header.offsetHeight + 60;
      header.classList.toggle('is-hidden', goingDown && !header.dataset.preventHide);
    }
    lastY = y;
  };

  window.addEventListener('scroll', throttle(onScroll, 80), { passive: true });
  onScroll();
}

/* --------------------------------------------------------------------------
   Page transition fade
   -------------------------------------------------------------------------- */
function initPageFade() {
  if (!document.body.classList.contains('anim-page-fade') || prefersReducedMotion()) return;

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a');
    if (!link) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    if (link.target === '_blank' || link.hasAttribute('download') || link.dataset.noTransition === 'true') return;

    const href = link.getAttribute('href');
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return;

    let url;
    try {
      url = new URL(link.href, window.location.origin);
    } catch (error) {
      return;
    }
    if (url.origin !== window.location.origin) return;
    if (url.pathname === window.location.pathname && url.search === window.location.search) return;
    if (url.pathname.startsWith('/checkout') || url.pathname.startsWith('/cart')) return;

    document.body.classList.add('is-leaving');
    window.setTimeout(() => document.body.classList.remove('is-leaving'), 1200);
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) document.body.classList.remove('is-leaving');
  });
}

/* --------------------------------------------------------------------------
   Quantity inputs (used by cart, product, quick view)
   -------------------------------------------------------------------------- */
/** Enable or disable a stepper's buttons for the value it currently holds. */
function syncQuantity(wrapper) {
  const input = wrapper.querySelector('input');
  if (!input) return;
  const min = Number(input.min || 1);
  const max = input.max ? Number(input.max) : Infinity;
  const value = Number(input.value || min);
  const minus = wrapper.querySelector('[data-quantity-minus]');
  const plus = wrapper.querySelector('[data-quantity-plus]');
  if (minus) minus.disabled = value <= min;
  if (plus) plus.disabled = value >= max;
}

/** Refresh the button states after markup has been swapped in. */
export function syncQuantityInputs(root = document) {
  const scope = root && root.querySelectorAll ? root : document;
  scope.querySelectorAll('[data-quantity]').forEach(syncQuantity);
}

/**
 * Quantity steppers.
 *
 * Delegated from the document rather than bound per element, because the cart
 * replaces its own markup after every change: a per-element listener works for
 * the first click and is then thrown away with the node it was attached to,
 * which is exactly how the plus and minus buttons came to stop responding
 * after one use.
 */
function initQuantityInputs() {
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-quantity-plus], [data-quantity-minus]');
    if (!button || button.disabled) return;
    const wrapper = button.closest('[data-quantity]');
    const input = wrapper && wrapper.querySelector('input');
    if (!input) return;

    const direction = button.hasAttribute('data-quantity-plus') ? 1 : -1;
    const stepSize = Number(input.step || 1);
    const min = Number(input.min || 1);
    const max = input.max ? Number(input.max) : Infinity;
    let next = Number(input.value || min) + direction * stepSize;
    next = Math.min(max, Math.max(min, next));
    if (next === Number(input.value)) return;

    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    syncQuantity(wrapper);
  });

  document.addEventListener('change', (event) => {
    const target = event.target;
    const wrapper = target && target.closest ? target.closest('[data-quantity]') : null;
    if (wrapper) syncQuantity(wrapper);
  });

  syncQuantityInputs();
}

/* --------------------------------------------------------------------------
   Star ratings from data attributes (avoids inline styles in Liquid)
   -------------------------------------------------------------------------- */
function initRatings(root = document) {
  root.querySelectorAll('[data-rating-value]').forEach((el) => {
    const value = parseFloat(el.dataset.ratingValue);
    const max = parseFloat(el.dataset.ratingMax || '5');
    if (Number.isNaN(value) || !max) return;
    const fill = el.querySelector('.rating__fill');
    if (fill) fill.style.setProperty('--rating', `${Math.max(0, Math.min(100, (value / max) * 100))}%`);
  });
}

/* --------------------------------------------------------------------------
   Colour swatch previews from data attributes
   -------------------------------------------------------------------------- */
function initSwatches(root = document) {
  root.querySelectorAll('[data-swatch]').forEach((el) => {
    const value = el.dataset.swatch;
    if (!value) return;
    if (value.startsWith('http') || value.startsWith('//') || value.startsWith('/')) {
      el.style.setProperty('--swatch', `url("${value}")`);
    } else {
      el.style.setProperty('--swatch', value);
    }
  });
}

/* --------------------------------------------------------------------------
   Magnetic buttons (only when that hover style is chosen)
   -------------------------------------------------------------------------- */
function initMagneticButtons() {
  if (!document.body.classList.contains('btn-hover-magnetic')) return;
  if (!document.body.classList.contains('anim-button-hover')) return;
  if (prefersReducedMotion()) return;
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  document.addEventListener('pointermove', (event) => {
    const btn = event.target.closest('.btn');
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    const x = (event.clientX - rect.left - rect.width / 2) * 0.18;
    const y = (event.clientY - rect.top - rect.height / 2) * 0.3;
    btn.style.transform = `translate(${x}px, ${y}px)`;
  });

  document.addEventListener('pointerout', (event) => {
    const btn = event.target.closest('.btn');
    if (btn) btn.style.transform = '';
  });
}

/* --------------------------------------------------------------------------
   Copy-to-clipboard buttons
   -------------------------------------------------------------------------- */
function initCopyButtons() {
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest('[data-copy]');
    if (!btn) return;
    event.preventDefault();
    const value = btn.dataset.copy === 'url' ? window.location.href : btn.dataset.copy;
    try {
      await navigator.clipboard.writeText(value);
      const { toast } = await import('./utils.js');
      toast(config.strings.copied || 'Copied', 'success', 2000);
    } catch (error) {
      /* clipboard blocked — nothing else to do */
    }
  });
}

/* --------------------------------------------------------------------------
   Click-to-play video embeds (nothing third-party loads before the click)
   -------------------------------------------------------------------------- */
function initVideoEmbeds() {
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-video-play]');
    if (!button) return;
    const wrapper = button.closest('[data-video]');
    const template = wrapper && wrapper.querySelector('[data-video-embed]');
    if (!template) return;
    event.preventDefault();
    const frame = template.content.cloneNode(true);
    button.remove();
    wrapper.appendChild(frame);
  });
}

/* --------------------------------------------------------------------------
   Print buttons
   -------------------------------------------------------------------------- */
function initPrintButtons() {
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-print]')) return;
    window.print();
  });
}

/* --------------------------------------------------------------------------
   Lazy feature loading
   -------------------------------------------------------------------------- */
async function loadFeatures() {
  const tasks = [];

  if (document.querySelector('[data-cart-root], [data-add-to-cart], [data-cart-drawer], [data-terms-gate]')) {
    tasks.push(import('./cart.js').then((m) => m.init()));
  }
  if (document.querySelector('[data-product-form], [data-product-media]')) {
    tasks.push(import('./product.js').then((m) => m.init()));
  }
  if (document.querySelector('[data-predictive-search]')) {
    tasks.push(import('./search.js').then((m) => m.init()));
  }
  if (document.querySelector('[data-facets]')) {
    tasks.push(import('./facets.js').then((m) => m.init()));
  }
  if (document.querySelector('[data-header]')) {
    tasks.push(import('./header.js').then((m) => m.init()));
  }
  if (document.querySelector('[data-carousel]')) {
    tasks.push(import('./carousel.js').then((m) => m.init()));
  }
  if (document.querySelector('[data-countdown]')) {
    tasks.push(import('./countdown.js').then((m) => m.init()));
  }
  if (document.querySelector('[data-tabs]')) {
    tasks.push(import('./tabs.js').then((m) => m.init()));
  }
  if (document.querySelector('[data-localization-form]')) {
    tasks.push(import('./localization.js').then((m) => m.init()));
  }

  await Promise.allSettled(tasks);
}

/* --------------------------------------------------------------------------
   Boot
   -------------------------------------------------------------------------- */
function boot() {
  // Everything below registers document- or window-level listeners, and
  // loadFeatures() calls every feature module's init(). Running it twice binds
  // all of it twice.
  if (!once('boot')) return;
  initReveal();
  initAccordions();
  initPanelTriggers();
  initStickyHeader();
  initPageFade();
  initQuantityInputs();
  initRatings();
  initSwatches();
  initMagneticButtons();
  initCopyButtons();
  initVideoEmbeds();
  initPrintButtons();
  loadFeatures();

  // Re-run the per-node initialisers whenever the editor swaps a section.
  onSectionLoad((node) => {
    if (node === document) return;
    initReveal(node);
    initAccordions(node);
    syncQuantityInputs(node);
    initRatings(node);
    initSwatches(node);
  });

  if (isDesignMode()) {
    document.addEventListener('shopify:section:select', (event) => {
      const panel = event.target.querySelector('.drawer, .modal');
      if (panel && event.detail && event.detail.load === false) openPanel(panel);
    });
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

window.Onyx = { openPanel, closePanel, closeAllPanels, config };
